import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Image,
  Badge,
  Button,
  Modal,
  Carousel
} from 'react-bootstrap';
import { 
  FaSwimmingPool,
  FaBook,
  FaLaptop,
  FaBuilding,
  FaGraduationCap,
  FaArrowRight,
  FaStar,
  FaUsers,
  FaClock,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaImages,
  FaMapMarkerAlt,
  FaCheck,
  FaPlus,
  FaHeart,
  FaShareAlt,
  FaInfoCircle
} from 'react-icons/fa';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

// Move facilityIcons outside the component
const FACILITY_ICONS = {
  'swimming': <FaSwimmingPool />,
  'pool': <FaSwimmingPool />,
  'library': <FaBook />,
  'computer': <FaLaptop />,
  'lab': <FaLaptop />,
  'building': <FaBuilding />,
  'college': <FaGraduationCap />,
  'default': <FaBuilding />
};

const PublicFacilities = () => {
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [favorites, setFavorites] = useState([]);

  // API configuration
  const API_URL = `${BASE_URL}/facilities.php`;

  // Gradient backgrounds for cards
  const cardGradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)'
  ];

  // Helper functions wrapped in useCallback
  const getShortDescription = useCallback((description) => {
    if (!description) return 'No description available';
    return description.length > 120 ? description.substring(0, 120) + '...' : description;
  }, []);

  const getImageUrl = useCallback((path) => {
    if (!path) return null;
    return `${UPLOAD_BASE_URL}${path}`;
  }, []);

  const getFacilityIcon = useCallback((facilityName) => {
    const lowerName = facilityName.toLowerCase();
    for (const [key, icon] of Object.entries(FACILITY_ICONS)) {
      if (lowerName.includes(key)) return icon;
    }
    return FACILITY_ICONS.default;
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Recently added';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }, []);

  const isFavorite = useCallback((facilityId) => {
    return favorites.includes(facilityId);
  }, [favorites]);

  const toggleFavorite = useCallback((facilityId) => {
    setFavorites(prev => 
      prev.includes(facilityId) 
        ? prev.filter(id => id !== facilityId)
        : [...prev, facilityId]
    );
  }, []);

  // Fetch facilities function
  const fetchFacilities = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(API_URL);
      const result = await response.json();

      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        // Filter only active facilities and enhance with additional data
        const activeFacilities = result.data.filter(facility => facility.status === 'Active');
        
        const enhancedFacilities = activeFacilities.map((facility, index) => ({
          ...facility,
          id: parseInt(facility.id),
          rating: (4 + Math.random()).toFixed(1),
          visitorCount: Math.floor(Math.random() * 1000) + 500,
          photosCount: facility.photos ? facility.photos.length : 0,
          lastUpdated: facility.updated_at || facility.uploaded_at,
          shortDescription: getShortDescription(facility.description),
          gradient: cardGradients[index % cardGradients.length]
        }));
        
        setFacilities(enhancedFacilities);
        setFilteredFacilities(enhancedFacilities);
      } else {
        setError('No facilities available');
        setFacilities([]);
        setFilteredFacilities([]);
      }
    } catch (err) {
      console.error('Error fetching facilities:', err);
      setError('Failed to load facilities. Please try again later.');
      setFacilities([]);
      setFilteredFacilities([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getShortDescription]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  // Filter facilities based on search and filter
  useEffect(() => {
    let filtered = facilities;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(facility =>
        facility.facility_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (facility.description && facility.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply category filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(facility => {
        const lowerName = facility.facility_name.toLowerCase();
        switch (activeFilter) {
          case 'recent':
            return new Date(facility.uploaded_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
          case 'popular':
            return facility.rating >= 4.5;
          case 'with-photos':
            return facility.photosCount > 0;
          default:
            return true;
        }
      });
    }
    
    setFilteredFacilities(filtered);
  }, [facilities, searchTerm, activeFilter]);

  const handleViewDetails = (facility) => {
    setSelectedFacility(facility);
    setSelectedPhotoIndex(0);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedFacility(null);
    setShowModal(false);
  };

  const handlePhotoSelect = (index) => {
    setSelectedPhotoIndex(index);
  };

  const handleShare = (facility) => {
    if (navigator.share) {
      navigator.share({
        title: facility.facility_name,
        text: facility.shortDescription,
        url: window.location.href,
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Inline styles object
  const styles = {
    portal: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      minHeight: '100vh'
    },
    hero: {
      background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
      position: 'relative',
      overflow: 'hidden'
    },
    heroBefore: {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
      opacity: 0.3
    },
    heroBadge: {
      fontSize: '0.9rem',
      padding: '8px 16px',
      borderRadius: '20px',
      background: 'rgba(255, 255, 255, 0.2)',
      border: '1px solid rgba(255, 255, 255, 0.3)'
    },
    textGradient: {
      background: 'linear-gradient(90deg, #64b5f6, #1976d2)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    heroStats: {
      display: 'flex',
      gap: '30px',
      marginTop: '30px'
    },
    heroStatItem: {
      display: 'flex',
      flexDirection: 'column'
    },
    heroVisual: {
      position: 'relative',
      height: '300px'
    },
    floatingShape: {
      position: 'absolute',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
      animation: 'float 6s ease-in-out infinite'
    },
    shape1: {
      width: '100px',
      height: '100px',
      top: '20px',
      right: '50px',
      animationDelay: '0s'
    },
    shape2: {
      width: '70px',
      height: '70px',
      bottom: '40px',
      right: '100px',
      animationDelay: '2s'
    },
    shape3: {
      width: '50px',
      height: '50px',
      top: '100px',
      right: '150px',
      animationDelay: '4s'
    },
    previewCard: {
      position: 'absolute',
      top: '50%',
      left: 0,
      transform: 'translateY(-50%)',
      background: 'rgba(255, 255, 255, 0.95)',
      padding: '20px',
      borderRadius: '15px',
      boxShadow: '0 15px 35px rgba(0, 0, 0, 0.2)',
      width: '200px'
    },
    previewIcon: {
      width: '50px',
      height: '50px',
      background: 'linear-gradient(135deg, #1a237e, #283593)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '24px',
      marginBottom: '15px'
    },
    searchSection: {
      background: 'white',
      padding: '20px',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)'
    },
    searchBox: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    },
    searchIcon: {
      position: 'absolute',
      left: '20px',
      color: '#6c757d',
      zIndex: 2
    },
    searchInput: {
      width: '100%',
      padding: '15px 20px 15px 50px',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
      paddingRight: '100px'
    },
    filterTabs: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end'
    },
    filterTab: {
      padding: '10px 15px',
      borderRadius: '8px',
      background: '#f8f9fa',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.9rem'
    },
    facilityCard: {
      borderRadius: '16px',
      overflow: 'hidden',
      transition: 'all 0.4s ease',
      height: '100%'
    },
    facilityCardHeader: {
      position: 'relative',
      height: '200px',
      overflow: 'hidden'
    },
    placeholderImage: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      textAlign: 'center'
    },
    placeholderIcon: {
      fontSize: '48px',
      color: 'white',
      marginBottom: '15px'
    },
    facilityBadges: {
      position: 'absolute',
      top: '15px',
      left: '15px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      alignItems: 'flex-start'
    },
    favoriteBtn: {
      position: 'absolute',
      top: '15px',
      right: '15px',
      color: 'white',
      fontSize: '1.5rem',
      padding: '5px',
      background: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease'
    },
    facilityTitle: {
      fontWeight: 600,
      color: '#1a237e'
    },
    thumbnailsWrapper: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap'
    },
    thumbnailItem: {
      width: '80px',
      height: '60px',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    thumbnailMore: {
      width: '80px',
      height: '60px',
      borderRadius: '8px',
      background: '#f8f9fa',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: '#6c757d'
    },
    loadingDots: {
      display: 'flex',
      gap: '10px'
    },
    dot: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #1a237e, #283593)',
      animation: 'bounce 1.4s infinite ease-in-out'
    },
    featureCard: {
      background: 'white',
      padding: '30px',
      borderRadius: '15px',
      textAlign: 'center',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      height: '100%'
    },
    featureIcon: {
      width: '70px',
      height: '70px',
      margin: '0 auto 20px',
      background: 'linear-gradient(135deg, #1a237e, #283593)',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '28px'
    },
    modalFacilityIcon: {
      width: '60px',
      height: '60px',
      borderRadius: '15px',
      background: 'linear-gradient(135deg, #1a237e, #283593)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '28px'
    },
    carouselContainer: {
      height: '400px',
      overflow: 'hidden',
      borderRadius: '12px'
    },
    carouselThumbnails: {
      display: 'flex',
      gap: '10px',
      marginTop: '15px',
      overflowX: 'auto',
      padding: '10px 0'
    },
    thumbnail: {
      width: '80px',
      height: '60px',
      borderRadius: '8px',
      overflow: 'hidden',
      cursor: 'pointer',
      opacity: 0.6,
      transition: 'all 0.3s ease'
    },
    noPhotosPlaceholder: {
      textAlign: 'center',
      padding: '40px',
      background: '#f8f9fa',
      borderRadius: '12px'
    },
    modalStats: {
      display: 'flex',
      gap: '30px',
      marginBottom: '30px'
    },
    modalStatItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      padding: '15px',
      background: '#f8f9fa',
      borderRadius: '12px',
      minWidth: '120px'
    },
    galleryThumbnail: {
      position: 'relative',
      cursor: 'pointer',
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'all 0.3s ease'
    },
    facilityDetails: {
      background: '#f8f9fa',
      padding: '20px',
      borderRadius: '12px',
      marginTop: '30px'
    },
    noResults: {
      textAlign: 'center',
      padding: '60px 20px'
    }
  };

  // Keyframes for animations
  const keyframes = `
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-20px); }
    }
    @keyframes bounce {
      0%, 80%, 100% { transform: scale(0); }
      40% { transform: scale(1); }
    }
  `;

  if (loading) {
    return (
      <Container className="facilities-container py-5">
        <style>{keyframes}</style>
        <div className="text-center">
          <div className="loading-animation" style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={styles.loadingDots}>
              <div style={{ ...styles.dot, animationDelay: '-0.32s' }}></div>
              <div style={{ ...styles.dot, animationDelay: '-0.16s' }}></div>
              <div style={styles.dot}></div>
            </div>
          </div>
          <h4 className="mt-4" style={styles.textGradient}>Discovering Our Facilities</h4>
          <p className="text-muted">Loading state-of-the-art campus amenities...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="border-0 shadow-sm">
          <Alert.Heading>Unable to Load Facilities</Alert.Heading>
          <p>{error}</p>
          <Button variant="primary" onClick={fetchFacilities}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div style={styles.portal}>
      <style>{keyframes}</style>
      
      {/* Hero Section */}
      <div style={styles.hero}>
        <div style={styles.heroBefore}></div>
        <Container className="py-5">
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <Badge style={styles.heroBadge} className="mb-3">
                <FaBuilding className="me-2" /> Campus Facilities
              </Badge>
              <h1 className="display-5 fw-bold text-white mb-3">
                Our <span style={styles.textGradient}>Facilities</span> 
              </h1>
              <p className="lead text-white-50 mb-4">
                Discover state-of-the-art amenities designed to enhance your learning 
                experience and campus life.
              </p>
              <div style={styles.heroStats}>
                <div style={styles.heroStatItem}>
                  <h3 className="text-white mb-0">{facilities.length}+</h3>
                  <small className="text-white-50">Facilities</small>
                </div>
                <div style={styles.heroStatItem}>
                  <h3 className="text-white mb-0">
                    {facilities.reduce((sum, f) => sum + f.photosCount, 0)}+
                  </h3>
                  <small className="text-white-50">Photos</small>
                </div>
                <div style={styles.heroStatItem}>
                  <h3 className="text-white mb-0">100%</h3>
                  <small className="text-white-50">Accessibility</small>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div style={styles.heroVisual}>
                <div style={{ ...styles.floatingShape, ...styles.shape1 }}></div>
                <div style={{ ...styles.floatingShape, ...styles.shape2 }}></div>
                <div style={{ ...styles.floatingShape, ...styles.shape3 }}></div>
                <div style={styles.previewCard}>
                  <div style={styles.previewIcon}>
                    <FaGraduationCap />
                  </div>
                  <h5 className="mb-2">Premium Facilities</h5>
                  <p className="small mb-0">Modern amenities for enhanced learning</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {/* Search and Filter Section */}
        <div style={styles.searchSection} className="mb-5">
          <Row className="align-items-center">
            <Col lg={8}>
              <div className="search-wrapper">
                <div style={styles.searchBox}>
                  <div style={styles.searchIcon}>
                    <FaSearch />
                  </div>
                  <input
                    type="text"
                    style={styles.searchInput}
                    placeholder="Search facilities by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button 
                      variant="link" 
                      className="search-clear"
                      onClick={() => setSearchTerm('')}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={4}>
              <div style={styles.filterTabs}>
                <div 
                  style={{
                    ...styles.filterTab,
                    background: activeFilter === 'all' ? '#1a237e' : '#f8f9fa',
                    color: activeFilter === 'all' ? 'white' : 'inherit'
                  }}
                  onClick={() => setActiveFilter('all')}
                >
                  <FaFilter className="me-2" />
                  All Facilities
                </div>
                <div 
                  style={{
                    ...styles.filterTab,
                    background: activeFilter === 'recent' ? '#1a237e' : '#f8f9fa',
                    color: activeFilter === 'recent' ? 'white' : 'inherit'
                  }}
                  onClick={() => setActiveFilter('recent')}
                >
                  <FaCalendarAlt className="me-2" />
                  Recent
                </div>
                <div 
                  style={{
                    ...styles.filterTab,
                    background: activeFilter === 'with-photos' ? '#1a237e' : '#f8f9fa',
                    color: activeFilter === 'with-photos' ? 'white' : 'inherit'
                  }}
                  onClick={() => setActiveFilter('with-photos')}
                >
                  <FaImages className="me-2" />
                  With Photos
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Facilities Grid */}
        <Row className="g-4">
          {filteredFacilities.map((facility) => (
            <Col lg={4} md={6} key={facility.id}>
              <div className="facility-card-wrapper">
                <Card className="border-0 shadow-lg" style={styles.facilityCard}>
                  {/* Card Header with Image */}
                  <div style={styles.facilityCardHeader}>
                    {facility.photos && facility.photos.length > 0 ? (
                      <Image
                        src={getImageUrl(facility.photos[0].photo_path)}
                        alt={facility.photos[0].alt_text || facility.facility_name}
                        fluid
                        className="facility-main-image"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://via.placeholder.com/400x250?text=${facility.facility_name}`;
                        }}
                      />
                    ) : (
                      <div 
                        style={{ ...styles.placeholderImage, background: facility.gradient }}
                      >
                        <div style={styles.placeholderIcon}>
                          {getFacilityIcon(facility.facility_name)}
                        </div>
                        <h5 className="text-white mb-0">{facility.facility_name}</h5>
                      </div>
                    )}
                    
                    {/* Overlay Badges */}
                    <div style={styles.facilityBadges}>
                      <Badge bg="light" text="dark" className="facility-rating-badge">
                        <FaStar className="text-warning me-1" />
                        {facility.rating}
                      </Badge>
                      {facility.photosCount > 0 && (
                        <Badge bg="info" className="photos-badge">
                          <FaImages className="me-1" />
                          {facility.photosCount} photos
                        </Badge>
                      )}
                    </div>
                    
                    {/* Favorite Button */}
                    <Button 
                      variant="link" 
                      style={styles.favoriteBtn}
                      onClick={() => toggleFavorite(facility.id)}
                    >
                      <FaHeart className={isFavorite(facility.id) ? 'text-danger' : 'text-white'} />
                    </Button>
                  </div>
                  
                  <Card.Body className="p-4">
                    {/* Facility Name and Description */}
                    <h4 style={styles.facilityTitle} className="mb-3">{facility.facility_name}</h4>
                    
                    <p className="facility-description text-muted mb-4">
                      {facility.shortDescription}
                    </p>
                    
                    {/* Facility Stats */}
                    <div className="facility-stats mb-4">
                      <div className="stat-item" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaUsers style={{ fontSize: '20px', color: '#1a237e' }} />
                        <div>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{facility.visitorCount}+</span>
                          <small className="stat-label">Visitors</small>
                        </div>
                      </div>
                      <div className="stat-item" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaCalendarAlt style={{ fontSize: '20px', color: '#1a237e' }} />
                        <div>
                          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{formatDate(facility.lastUpdated)}</span>
                          <small className="stat-label">Updated</small>
                        </div>
                      </div>
                    </div>
                    
                    {/* Thumbnail Photos (if available) */}
                    {facility.photos && facility.photos.length > 0 && (
                      <div className="thumbnail-photos mb-4">
                        <h6 className="mb-3">
                          <FaImages className="me-2" />
                          Gallery Preview
                        </h6>
                        <div style={styles.thumbnailsWrapper}>
                          {facility.photos.slice(0, 4).map((photo, index) => (
                            <div 
                              key={photo.id} 
                              style={styles.thumbnailItem}
                              onClick={() => handleViewDetails(facility)}
                            >
                              <Image
                                src={getImageUrl(photo.photo_path)}
                                alt={photo.alt_text || `Photo ${index + 1}`}
                                rounded
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://via.placeholder.com/80x60?text=Photo+${index + 1}`;
                                }}
                              />
                            </div>
                          ))}
                          {facility.photos.length > 4 && (
                            <div 
                              style={styles.thumbnailMore} 
                              onClick={() => handleViewDetails(facility)}
                            >
                              <FaPlus />
                              <span>{facility.photos.length - 4}+</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between align-items-center">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleShare(facility)}
                      >
                        <FaShareAlt className="me-2" />
                        Share
                      </Button>
                      
                      <Button
                        variant="primary"
                        className="view-details-btn"
                        onClick={() => handleViewDetails(facility)}
                      >
                        View Details
                        <FaArrowRight className="ms-2" />
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          ))}
        </Row>

        {/* No Results Message */}
        {filteredFacilities.length === 0 && (
          <div style={styles.noResults}>
            <div className="no-results-icon mb-4">
              <FaSearch size={60} className="text-muted" />
            </div>
            <h4 className="mb-3">No Facilities Found</h4>
            <p className="text-muted mb-4">
              {searchTerm 
                ? `No facilities match "${searchTerm}"`
                : "No facilities are currently available"}
            </p>
            <Button 
              variant="outline-primary" 
              onClick={() => {
                setSearchTerm('');
                setActiveFilter('all');
              }}
            >
              Show All Facilities
            </Button>
          </div>
        )}

        {/* Campus Facilities Info */}
        <Row className="mt-5 pt-5">
          <Col lg={12}>
            <div className="section-header text-center mb-5">
              <Badge bg="light" text="dark" className="mb-3 px-3 py-2">
                <FaInfoCircle className="me-2" /> Campus Features
              </Badge>
              <h2 className="mb-3">Premium Campus Experience</h2>
              <p className="text-muted">Modern facilities designed for excellence</p>
            </div>
            
            <Row className="g-4">
              <Col lg={3} md={6}>
                <div style={styles.featureCard}>
                  <div style={styles.featureIcon}>
                    <FaCheck />
                  </div>
                  <h5>Quality Standards</h5>
                  <p className="text-muted small">All facilities meet international quality standards</p>
                </div>
              </Col>
              <Col lg={3} md={6}>
                <div style={styles.featureCard}>
                  <div style={styles.featureIcon}>
                    <FaClock />
                  </div>
                  <h5>24/7 Access</h5>
                  <p className="text-muted small">Round-the-clock access to key facilities</p>
                </div>
              </Col>
              <Col lg={3} md={6}>
                <div style={styles.featureCard}>
                  <div style={styles.featureIcon}>
                    <FaMapMarkerAlt />
                  </div>
                  <h5>Central Location</h5>
                  <p className="text-muted small">All facilities centrally located on campus</p>
                </div>
              </Col>
              <Col lg={3} md={6}>
                <div style={styles.featureCard}>
                  <div style={styles.featureIcon}>
                    <FaImages />
                  </div>
                  <h5>Photo Gallery</h5>
                  <p className="text-muted small">Comprehensive photo documentation</p>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Facility Detail Modal */}
      {selectedFacility && (
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          centered 
          size="lg"
          className="facility-detail-modal"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title>
              <div className="d-flex align-items-center">
                <div style={styles.modalFacilityIcon} className="me-3">
                  {getFacilityIcon(selectedFacility.facility_name)}
                </div>
                <div>
                  <h3 className="mb-0">{selectedFacility.facility_name}</h3>
                  <div className="modal-subtitle">
                    <Badge bg={selectedFacility.status === 'Active' ? 'success' : 'secondary'}>
                      {selectedFacility.status}
                    </Badge>
                    <span className="text-muted ms-3">
                      <FaCalendarAlt className="me-1" />
                      {formatDate(selectedFacility.uploaded_at)}
                    </span>
                  </div>
                </div>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {/* Photo Carousel */}
            {selectedFacility.photos && selectedFacility.photos.length > 0 ? (
              <div className="modal-carousel mb-4">
                <Carousel 
                  activeIndex={selectedPhotoIndex}
                  onSelect={handlePhotoSelect}
                  interval={null}
                  indicators={selectedFacility.photos.length > 1}
                  controls={selectedFacility.photos.length > 1}
                >
                  {selectedFacility.photos.map((photo, index) => (
                    <Carousel.Item key={photo.id}>
                      <div style={styles.carouselContainer}>
                        <Image
                          src={getImageUrl(photo.photo_path)}
                          alt={photo.alt_text || `Photo ${index + 1}`}
                          fluid
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://via.placeholder.com/800x450?text=${selectedFacility.facility_name}`;
                          }}
                        />
                      </div>
                      {photo.alt_text && (
                        <Carousel.Caption className="carousel-caption">
                          <p className="mb-0">{photo.alt_text}</p>
                        </Carousel.Caption>
                      )}
                    </Carousel.Item>
                  ))}
                </Carousel>
                
                {/* Thumbnail Strip */}
                {selectedFacility.photos.length > 1 && (
                  <div style={styles.carouselThumbnails}>
                    {selectedFacility.photos.map((photo, index) => (
                      <div 
                        key={photo.id}
                        style={{
                          ...styles.thumbnail,
                          opacity: index === selectedPhotoIndex ? 1 : 0.6,
                          border: index === selectedPhotoIndex ? '2px solid #1a237e' : 'none'
                        }}
                        onClick={() => handlePhotoSelect(index)}
                      >
                        <Image
                          src={getImageUrl(photo.photo_path)}
                          alt={`Thumbnail ${index + 1}`}
                          fluid
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.noPhotosPlaceholder} className="mb-4">
                <div style={{ fontSize: '72px', color: '#1a237e' }}>
                  {getFacilityIcon(selectedFacility.facility_name)}
                </div>
                <p className="text-center text-muted mt-3">No photos available</p>
              </div>
            )}
            
            <div className="modal-content">
              {/* Facility Stats */}
              <div style={styles.modalStats}>
                <div style={styles.modalStatItem}>
                  <div>
                    <FaStar style={{ fontSize: '24px', color: '#ffc107' }} />
                  </div>
                  <div>
                    <h6 className="mb-0">{selectedFacility.rating}/5</h6>
                    <small>Rating</small>
                  </div>
                </div>
                <div style={styles.modalStatItem}>
                  <div>
                    <FaUsers style={{ fontSize: '24px', color: '#007bff' }} />
                  </div>
                  <div>
                    <h6 className="mb-0">{selectedFacility.visitorCount}+</h6>
                    <small>Visitors</small>
                  </div>
                </div>
                <div style={styles.modalStatItem}>
                  <div>
                    <FaImages style={{ fontSize: '24px', color: '#17a2b8' }} />
                  </div>
                  <div>
                    <h6 className="mb-0">{selectedFacility.photosCount}</h6>
                    <small>Photos</small>
                  </div>
                </div>
              </div>
              
              {/* Full Description */}
              <h5 className="mb-3">About This Facility</h5>
              <div className="facility-full-description">
                {selectedFacility.description || 'No detailed description available.'}
              </div>
              
              {/* Photo Gallery Grid */}
              {selectedFacility.photos && selectedFacility.photos.length > 0 && (
                <>
                  <h5 className="mt-4 mb-3">Photo Gallery</h5>
                  <Row className="g-3">
                    {selectedFacility.photos.map((photo) => (
                      <Col xs={4} md={3} key={photo.id}>
                        <div 
                          style={styles.galleryThumbnail}
                          onClick={() => handlePhotoSelect(selectedFacility.photos.findIndex(p => p.id === photo.id))}
                        >
                          <Image
                            src={getImageUrl(photo.photo_path)}
                            alt={photo.alt_text || 'Facility photo'}
                            fluid
                            rounded
                            style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                          />
                          {photo.alt_text && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              padding: '5px',
                              fontSize: '0.75rem'
                            }}>
                              <small>{photo.alt_text}</small>
                            </div>
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
              
              {/* Facility Details */}
              <div style={styles.facilityDetails}>
                <h5 className="mb-3">Facility Details</h5>
                <Row>
                  <Col md={6}>
                    <div className="detail-item">
                      <strong>Status:</strong>
                      <Badge bg={selectedFacility.status === 'Active' ? 'success' : 'secondary'} className="ms-2">
                        {selectedFacility.status}
                      </Badge>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <strong>Added:</strong>
                      <span className="ms-2">{formatDate(selectedFacility.uploaded_at)}</span>
                    </div>
                  </Col>
                  {selectedFacility.updated_at && (
                    <Col md={6}>
                      <div className="detail-item">
                        <strong>Last Updated:</strong>
                        <span className="ms-2">{formatDate(selectedFacility.updated_at)}</span>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <div className="d-flex justify-content-between w-100">
              <div>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => toggleFavorite(selectedFacility.id)}
                >
                  <FaHeart className={isFavorite(selectedFacility.id) ? 'text-danger' : ''} />
                  <span className="ms-2">
                    {isFavorite(selectedFacility.id) ? 'Remove from Favorites' : 'Add to Favorites'}
                  </span>
                </Button>
                <Button 
                  variant="outline-primary" 
                  className="ms-2"
                  onClick={() => handleShare(selectedFacility)}
                >
                  <FaShareAlt className="me-2" />
                  Share
                </Button>
              </div>
              <Button variant="primary" onClick={handleCloseModal}>
                Close Details
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default PublicFacilities;